import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { FiCheck, FiX, FiEye, FiSearch, FiLoader } from 'react-icons/fi';

interface Merchant {
  uuid: string;
  business_name: string;
  business_type: string;
  registration_number: string;
  merchant_code: string;
  status: string;
  commission_rate: number;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

const Merchants: React.FC = () => {
  const { employee } = useAuth();
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees/merchants`);
      setMerchants(response.data.data.merchants);
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveMerchant = async (merchantUuid: string) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/employees/merchants/${merchantUuid}/approve`);
      alert('Merchant approved successfully');
      fetchMerchants();
      setSelectedMerchant(null);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to approve merchant');
    } finally {
      setActionLoading(false);
    }
  };

  const suspendMerchant = async (merchantUuid: string) => {
    const reason = prompt('Enter reason for suspension:');
    if (!reason) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/employees/merchants/${merchantUuid}/suspend`, { reason });
      alert('Merchant suspended successfully');
      fetchMerchants();
      setSelectedMerchant(null);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to suspend merchant');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredMerchants = merchants.filter(merchant =>
    merchant.business_name.toLowerCase().includes(search.toLowerCase()) ||
    merchant.email.toLowerCase().includes(search.toLowerCase()) ||
    merchant.merchant_code.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: 'pending',
      active: 'active',
      suspended: 'suspended',
      closed: 'closed'
    };
    return `status-badge ${statusMap[status] || 'pending'}`;
  };

  const getBusinessTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      retail: '🏪',
      restaurant: '🍽️',
      service: '🔧',
      ecommerce: '📦',
      government: '🏛️',
      bank: '🏦',
      ngo: '🤝',
      other: '📌'
    };
    return icons[type] || '📌';
  };

  if (loading) {
    return (
      <div className="dashboard">
        <main className="main-content" style={{ marginLeft: 0 }}>
          <div className="loading">Loading merchants...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <main className="main-content" style={{ marginLeft: 0 }}>
        <div className="page-header">
          <h1>Merchants</h1>
          <p>Manage merchant accounts and applications</p>
        </div>

        <div className="search-bar" style={{ marginBottom: 24, width: '100%', maxWidth: 400 }}>
          <FiSearch />
          <input
            type="text"
            placeholder="Search by business name, email, or merchant code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="merchants-grid">
          {filteredMerchants.map((merchant) => (
            <div key={merchant.uuid} className="merchant-card glass">
              <div className="merchant-card-header">
                <div className="merchant-icon">
                  {getBusinessTypeIcon(merchant.business_type)}
                </div>
                <div className="merchant-info">
                  <h3>{merchant.business_name}</h3>
                  <p className="merchant-code">Code: {merchant.merchant_code}</p>
                </div>
                <span className={getStatusBadge(merchant.status)}>
                  {merchant.status}
                </span>
              </div>
              
              <div className="merchant-details">
                <div className="detail-row">
                  <span>Owner:</span>
                  <strong>{merchant.first_name} {merchant.last_name}</strong>
                </div>
                <div className="detail-row">
                  <span>Email:</span>
                  <strong>{merchant.email}</strong>
                </div>
                <div className="detail-row">
                  <span>Phone:</span>
                  <strong>{merchant.phone}</strong>
                </div>
                <div className="detail-row">
                  <span>Type:</span>
                  <strong>{merchant.business_type}</strong>
                </div>
                <div className="detail-row">
                  <span>Commission:</span>
                  <strong>{merchant.commission_rate}%</strong>
                </div>
                <div className="detail-row">
                  <span>Registered:</span>
                  <strong>{new Date(merchant.created_at).toLocaleDateString()}</strong>
                </div>
              </div>

              <div className="merchant-actions">
                <button className="action-btn view" onClick={() => setSelectedMerchant(merchant)}>
                  <FiEye /> View Details
                </button>
                {merchant.status === 'pending' && (
                  <button 
                    className="action-btn approve" 
                    onClick={() => approveMerchant(merchant.uuid)}
                    disabled={actionLoading}
                  >
                    <FiCheck /> Approve
                  </button>
                )}
                {merchant.status === 'active' && (
                  <button 
                    className="action-btn suspend" 
                    onClick={() => suspendMerchant(merchant.uuid)}
                    disabled={actionLoading}
                  >
                    <FiX /> Suspend
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredMerchants.length === 0 && (
          <div className="empty-state glass">
            <h3>No merchants found</h3>
            <p>Try adjusting your search</p>
          </div>
        )}

        {selectedMerchant && (
          <div className="modal-overlay" onClick={() => setSelectedMerchant(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Merchant Details</h2>
              <div className="user-detail-info">
                <p><strong>Business Name:</strong> {selectedMerchant.business_name}</p>
                <p><strong>Business Type:</strong> {selectedMerchant.business_type}</p>
                <p><strong>Registration Number:</strong> {selectedMerchant.registration_number || '—'}</p>
                <p><strong>Merchant Code:</strong> {selectedMerchant.merchant_code}</p>
                <p><strong>Status:</strong> {selectedMerchant.status}</p>
                <p><strong>Commission Rate:</strong> {selectedMerchant.commission_rate}%</p>
                <p><strong>Owner:</strong> {selectedMerchant.first_name} {selectedMerchant.last_name}</p>
                <p><strong>Email:</strong> {selectedMerchant.email}</p>
                <p><strong>Phone:</strong> {selectedMerchant.phone}</p>
                <p><strong>Registered:</strong> {new Date(selectedMerchant.created_at).toLocaleString()}</p>
              </div>
              <div className="kyc-modal-actions">
                {selectedMerchant.status === 'pending' && (
                  <button 
                    className="kyc-approve-btn" 
                    onClick={() => approveMerchant(selectedMerchant.uuid)}
                    disabled={actionLoading}
                  >
                    Approve Merchant
                  </button>
                )}
                {selectedMerchant.status === 'active' && (
                  <button 
                    className="kyc-reject-btn" 
                    onClick={() => suspendMerchant(selectedMerchant.uuid)}
                    disabled={actionLoading}
                  >
                    Suspend Merchant
                  </button>
                )}
              </div>
              <button className="close-modal" onClick={() => setSelectedMerchant(null)}>Close</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Merchants;
